import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { todayRows, tonight, type RowClass, type TodayRow } from '../api/todayRows'
import { formatTime } from '../api/dayShape'
import { notInAnyClasses, youAreNotInAnyClasses } from '../api/portal'
import { friendlyDate, type TeacherSession } from '../api/teacher'
import type { PortalSession } from '../api/portal'

/**
 * Tonight, one line a person (D-380).
 *
 * **A snapshot, not a schedule.** `/calendar` already draws a dual-role
 * account's whole day — instructor *or* dancer, in one time-ordered view — and
 * `/schedule` already splits her two roles into named views. Home rebuilding
 * either would be a second implementation of a screen that exists, and two
 * implementations of one idea drift.
 *
 * **So no grid.** The design this replaced put one column per person, which is
 * four tracks and 80 pixels a column on a phone for Victoria Hogan, who teaches
 * twelve classes and has three children dancing. One line each reads the same
 * with one child or four.
 */
export default function TodayPanel({
  today,
  teaching,
  dancers,
  teaches = true,
  onTakeAttendance,
}: {
  today: string
  /** Today and the fortnight after it, so an empty day can say what is next. */
  teaching: TeacherSession[]
  dancers: { name: string; sessions: PortalSession[]; startsOn?: string | null; self?: boolean }[]
  /** False for a parent who teaches nothing, so she gets no teaching row. */
  teaches?: boolean
  /**
   * Open the register for one session (D-780).
   *
   * **Required, and it takes the session** — the two halves of the bug it
   * replaces. *Take attendance* was a `Link` to `/my-attendance` carrying
   * nothing, so every row on the evening led to the same list, and that list is
   * finished sessions only (D-685): a teacher tapping any of tonight's four
   * classes reached one class, the one that had already ended. Handing the id
   * to the caller keeps this panel pure the way `TodayForMe` splits it, and
   * makes "which class did she tap" a thing a test can hold.
   */
  onTakeAttendance: (occurrenceId: string) => void
}) {
  const rows = todayRows(today, teaching, dancers, teaches)
  const line = tonight(rows)

  if (rows.length === 0) return null

  return (
    <section className="mb-5">
      <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Today</h2>

      <div className="flex flex-col gap-2">
        {rows.map((row) =>
          row.classes.length > 0 ? (
            <Row key={row.key} row={row} onTakeAttendance={onTakeAttendance} />
          ) : (
            <QuietRow key={row.key} row={row} today={today} />
          ),
        )}
      </div>

      {line && (
        <p className="mt-2.5 rounded-xl border border-accent bg-accent-soft px-3.5 py-2.5 text-[13.5px] font-medium">
          {line}
        </p>
      )}
    </section>
  )
}

/** "17:00:00" as minutes, so `formatTime` writes it the way the rows do. */
const minutesOf = (time: string) => Number(time.split(':')[0]) * 60 + Number(time.split(':')[1])

/**
 * Somebody with nothing on today.
 *
 * **A statement, not a control** — there is nothing to expand, so it is not a
 * button. It appears at all because an absent row cannot be told apart from a
 * row that failed to load (D-209), and on a Wednesday one child dances while
 * another does not in ten of the twenty-seven households with two.
 */
function QuietRow({ row, today }: { row: TodayRow; today: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface px-3 py-2.5">
      <p className="flex items-baseline gap-2">
        <span className="text-[14.5px] font-semibold text-ink-2">{row.title}</span>
        <span className="ml-auto shrink-0 text-[12px] text-ink-3">nothing today</span>
      </p>

      {/*
        **No next date at all means she is in nothing this season** (D-384).

        `nextAfter` returns null only when the fortnight is empty *and* the
        server worked out no later start, so this is not "quiet week" — it is a
        dancer with no enrolment. Saying only "nothing today" would leave a
        parent to wonder whether the app had lost her, which is the D-209
        failure this row exists to prevent. The wording is the portal's own
        sentence for the same fact rather than a second way of saying it.

        A teacher's own row is exempt: "you are in no classes this season" is
        nonsense about the person holding the phone.

        **Asked of `self`, never of `subject`** (D-572). The exemption used to
        read `subject === 'dancer'`, which was the reader for exactly as long as
        a dancer row always belonged to somebody else. A dancer with her own
        login is a dancer row *and* the reader, so she was told "You are dancing
        is not in any classes this season" - her heading handed to a function
        that wanted a first name. Same defect as the evening sentence below, and
        the same fix: the row says whose it is rather than the words being
        matched.
      */}
      {!row.next && row.subject === 'dancer' && (
        <p className="mt-0.5 text-[12px] text-ink-3">
          {row.self ? youAreNotInAnyClasses() : notInAnyClasses(row.title)}
        </p>
      )}

      {row.next && (
        <p className="mt-0.5 text-[12px] text-ink-3">
          Next {friendlyDate(row.next.date, today).replace(/^on /, '')}
          {/*
            The count and the time come from sessions we actually hold. Past the
            fortnight either payload carries (D-176) the server knows only the
            date, so the row stops at the day rather than inventing the rest.
          */}
          {row.next.classes > 0 && (
            <>
              {' '}&middot; {row.next.classes}{' '}
              {row.next.classes === 1 ? 'class' : 'classes'}
              {row.next.from && <>, from {formatTime(minutesOf(row.next.from))}</>}
            </>
          )}
        </p>
      )}
    </div>
  )
}

function Row({
  row,
  onTakeAttendance,
}: {
  row: TodayRow
  onTakeAttendance: (occurrenceId: string) => void
}) {
  /*
   * Open when there is something to do, closed otherwise.
   *
   * Hannah Jones teaches twelve on a Monday, 4:00 to 9:15 with no gap anywhere,
   * and taking the register is the most-used action in the app. Putting it
   * behind a tap on the screen she opens every class night would be a
   * regression however tidy it looks. A teacher is working; a parent is
   * checking.
   */
  const [open, setOpen] = useState(row.classes.some((c) => c.needsMarking))

  const { spans, gaps } = row.sentence
  const toMark = row.classes.filter((c) => c.needsMarking).length

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`w-full rounded-xl border bg-surface px-3 py-2.5 text-left ${
          open ? 'rounded-b-none border-accent' : 'border-line hover:border-line-2'
        }`}
      >
        <span className="flex items-baseline gap-2">
          <ChevronRight
            size={13}
            strokeWidth={2.5}
            aria-hidden
            className={`shrink-0 self-center text-ink-3 transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <span className="text-[14.5px] font-semibold">{row.title}</span>
          <span className="ml-auto shrink-0 text-[12px] text-ink-3">
            {row.classes.length} {row.classes.length === 1 ? 'class' : 'classes'}
            {/*
              What `todaySummary` used to say in prose, on the row it belongs to
              (D-380, amending D-369). A count is scannable in a way a row of
              buttons is not — "3 classes, 2 to mark" is the whole of what she
              needs before walking in.
            */}
            {toMark > 0 && <span className="text-warn">, {toMark} to mark</span>}
          </span>
        </span>

        {/* The spans and the holes between them, interleaved. */}
        <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 pl-[21px] text-[12px] text-ink-2">
          {spans.map((span, i) => (
            <span key={span} className="contents">
              <span className="tabular">{span}</span>
              {gaps[i] && (
                <span className="rounded-full bg-warn-bg px-2 text-[11px] text-warn">{gaps[i]}</span>
              )}
            </span>
          ))}
        </span>
      </button>

      {open && (
        <div className="rounded-b-xl border border-t-0 border-accent bg-surface px-3 pb-2">
          {row.classes.map((c) => (
            <ClassLine
              key={c.occurrenceId + row.key}
              klass={c}
              onTakeAttendance={onTakeAttendance}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ClassLine({
  klass,
  onTakeAttendance,
}: {
  klass: RowClass
  onTakeAttendance: (occurrenceId: string) => void
}) {
  return (
    <div className="flex gap-2.5 border-t border-line py-2 first:border-t-0">
      {/* The studio colour-codes its rooms and every calendar reads the same
          value (D-176), so a parent matches the row to the door. */}
      <span
        aria-hidden
        className="mt-0.5 w-[3px] shrink-0 rounded-full bg-line-2"
        style={klass.roomColor ? { background: klass.roomColor } : undefined}
      />

      <div className="min-w-0">
        <p className="text-[11.5px] font-semibold tabular">{klass.when}</p>

        <p className="text-[13px] wrap-anywhere">
          {klass.label}
          {klass.detail && <span className="text-ink-3"> &mdash; {klass.detail}</span>}
        </p>

        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11.5px] text-ink-3">
          {klass.roomName && <span className="wrap-anywhere">{klass.roomName}</span>}
          {klass.taughtBy.length > 0 && <span>with {klass.taughtBy.join(', ')}</span>}

          {/*
            How far through, which the aggregate on the row cannot say. "2 to
            mark" is the same whether she has not started or is one dancer
            short, and the second is the case a count is for.
          */}
          {klass.marked && (
            <span className="tabular">
              {klass.marked.done} of {klass.marked.of} marked
            </span>
          )}

          {klass.note && <span className="text-accent">{klass.note}</span>}
        </p>

        {klass.isCancelled && (
          <p className="mt-1 text-[11.5px] font-semibold text-crit">Cancelled</p>
        )}

        {/*
          The action stays on the row rather than behind another tap. It is the
          most-used thing on this screen and the reason the row opens by itself.
        */}
        {/*
          <b>It opens this session's register, and it used to open a list</b>
          (D-780). Reported by the owner from the live studio: *"clicks take
          attendance ... it brings them to the missed attendance page and shows
          them only one class, no matter what class they click on."*

          Both halves are this one link. It was `to="/my-attendance"` with no
          session on it, so all four of a teacher's evening classes led to the
          same screen — and that screen is the *gaps* queue, which holds past
          sessions only (D-685), so at 5:30 it showed her the one class that had
          already finished and nothing she was about to teach.

          <b>The register itself, which is what `/schedule` already did.</b>
          Tapping a class on the calendar opens `RosterModal` for that
          occurrence; that path kept working throughout and is the specification
          here. A second way to reach a register would be the drift A-17
          forbids, so this asks for the same one.

          <b>Guarded on `registerId`, never on `needsMarking` alone.</b> A
          makeup private needs marking and its row id is a *booking* (D-733), so
          the id to open is a different field — and a control that opens nothing
          is worse than no control.
        */}
        {klass.needsMarking && klass.registerId && (
          <button
            type="button"
            onClick={() => onTakeAttendance(klass.registerId!)}
            // `py-1` for the height: measured 98x17 on a phone, and a touch
            // target wants 24 (D-255). It is the most-tapped control on a
            // teacher's home, so it is the wrong one to leave small.
            //
            // `cursor-pointer` and `text-left` because this was an `<a>` and is
            // now a `<button>`: Tailwind's preflight sets neither, so a button
            // takes the browser's arrow cursor and its centred text. Nothing
            // about it should look different to the teacher (D-780).
            className="mt-1 inline-block cursor-pointer py-1 text-left text-[11.5px] font-semibold text-accent"
          >
            Take attendance &rarr;
          </button>
        )}
      </div>
    </div>
  )
}
